<template>
  <UButton
    ref="button"
    v-bind="{ ...$attrs, ...forwardedProps }"
    :to="buttonTo"
    :href="undefined"
    :external="external ?? !useRouterLink"
    :tabindex="isLink && inactive ? -1 : String(tabindex)"
    :loading="isLoading"
    :aria-busy="isLoading || undefined"
    :on-click="handleClick"
    class="p-button touch-manipulation select-none"
    @click.capture="guardClick"
  >
    <template v-if="leadingButtonIcon" #leading>
      <FontAwesomeIcon
        :icon="leadingButtonIcon"
        :size="iconSize"
        aria-hidden="true"
        :class="iconAnimation"
      />
    </template>
    <template v-if="$slots.default" #default="slotProps"><slot v-bind="slotProps" /></template>
    <template v-if="trailingButtonIcon" #trailing>
      <FontAwesomeIcon
        :icon="trailingButtonIcon"
        :size="iconSize"
        aria-hidden="true"
        :class="iconAnimation"
      />
    </template>
  </UButton>
</template>

<script setup lang="ts">
import type { ComponentColor } from "@/config/component-colors"
import { computed, inject, ref } from "vue"
import { routerKey } from "vue-router"
import { reactiveOmit } from "@vueuse/core"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import UButton, { type ButtonProps } from "@nuxt/ui/components/Button.vue"

type ButtonColor = ComponentColor
type ButtonIcon = unknown[]

interface Props extends Omit<
  ButtonProps,
  "ui" | "color" | "activeColor" | "icon" | "leadingIcon" | "trailingIcon" | "loadingIcon"
> {
  color?: ButtonColor
  activeColor?: ButtonColor
  /** FontAwesome icon positioned using leading and trailing. */
  icon?: ButtonIcon
  leadingIcon?: ButtonIcon
  trailingIcon?: ButtonIcon
  loadingIcon?: ButtonIcon
  animate?: "spin" | "ping" | "pulse" | "bounce"
  tabindex?: string | number
}

const props = withDefaults(defineProps<Props>(), {
  as: "button",
  type: "button",
  tabindex: "0",
  variant: "solid",
  size: "md",
  color: "primary",
  active: undefined,
  external: undefined,
  locale: undefined,
  trailing: undefined
})

defineOptions({ inheritAttrs: false })

const router = inject(routerKey, undefined)
const destination = computed(() => props.to ?? props.href)
const isLink = computed(() => destination.value != null)
const autoLoading = ref(false)
const isLoading = computed(() => props.loading || autoLoading.value)
const inactive = computed(() => props.disabled || isLoading.value)
const iconSize = computed(() => (props.size === "sm" ? "xs" : props.size === "lg" ? "lg" : "1x"))
const forwardedProps = reactiveOmit(
  props,
  "icon",
  "leadingIcon",
  "trailingIcon",
  "loadingIcon",
  "onClick",
  "animate",
  "tabindex"
)
const useRouterLink = computed(
  () =>
    isLink.value &&
    !!router &&
    !props.external &&
    (typeof destination.value !== "string" ||
      !/^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(destination.value))
)
const buttonTo = computed(() =>
  useRouterLink.value || typeof destination.value === "string"
    ? destination.value
    : destination.value && "path" in destination.value
      ? destination.value.path
      : undefined
)
const isLeading = computed(() =>
  Boolean(
    props.leadingIcon ||
    (props.icon && (props.leading || !props.trailing)) ||
    (isLoading.value && !props.trailing)
  )
)
const leadingButtonIcon = computed(() =>
  isLeading.value
    ? ((isLoading.value
        ? props.loadingIcon || faSpinner
        : props.leadingIcon || props.icon) as IconProp)
    : undefined
)
const trailingButtonIcon = computed(() =>
  (props.icon && props.trailing) ||
  (isLoading.value && props.trailing && !isLeading.value) ||
  (props.trailingIcon && props.trailing !== false)
    ? ((isLoading.value && !isLeading.value
        ? props.loadingIcon || faSpinner
        : props.trailingIcon || props.icon) as IconProp)
    : undefined
)
const animationClasses: Record<string, string> = {
  spin: "animate-spin",
  ping: "animate-ping",
  pulse: "animate-pulse",
  bounce: "animate-bounce"
}
const iconAnimation = computed(() =>
  isLoading.value ? "animate-spin" : animationClasses[props.animate || ""]
)

async function handleClick(event: MouseEvent) {
  autoLoading.value = Boolean(props.loadingAuto)
  const callbacks = Array.isArray(props.onClick) ? props.onClick : [props.onClick]
  try {
    await Promise.all(callbacks.map((callback) => callback?.(event)))
  } finally {
    autoLoading.value = false
  }
}

function guardClick(event: MouseEvent) {
  if (inactive.value) {
    event.preventDefault()
    event.stopImmediatePropagation()
  }
}

const button = ref<InstanceType<typeof UButton>>()
function focus() {
  button.value?.$el.focus()
}
function click() {
  button.value?.$el.click()
}
defineExpose({ focus, click })
</script>
